import { PayloadAction } from "@reduxjs/toolkit";
import { Guid } from "guid-typescript";
import { takeLatest, all, call, put, select } from "redux-saga/effects";

import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { ILocalizable, commonMessages, ILocale, isDefaultLocale, isLocalizable } from "infrastructure/localization";
import { notification, INotification } from "infrastructure/notification";
import { IValidationIssue, ValidationIssueSeverity } from "infrastructure/validation";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";
import { IPickerSearchCriterion, applyPickerFilter } from "ui/shared/helpers/plex-components";
import { IIndustry, extractApiErrorMessageContent } from "ui/shared/models/common";

import { translationsManagerActions } from "./TranslationsManager.actions";
import { translationsManagerApi as api, ITermTranslationsGetRequest, ITermTranslationsApproveRequest } from "./TranslationsManager.api";
import { getTranslationModifyData, getValidationTag } from "./TranslationsManager.helpers";
import {
  ITranslationsManagerFilter,
  ITermTranslationRelationship,
  ITermTranslationRow,
  IChangeText,
  ITranslationsManagerFilterData,
  ITermTranslationModel,
  ITermTranslationModifyData,
  ITermTranslationErrorModel,
  ValidationTags,
  SelectorState,
  selectors
} from "./TranslationsManager.state";

function* getChangedRows() {
  const rows: ITermTranslationRow[] = yield select(selectors.getGridRows);
  return rows.filter(row => row.isTranslationChanged || row.isTranslationDefinitionChanged);
}

function* getNotValidRows() {
  const rows: ITermTranslationRow[] = yield select(selectors.getGridRows);
  return rows.filter(row => row.validationIssues.length);
}

function* applyTermTranslationsChanges(data: ITermTranslationModifyData[]) {
  yield put(translationsManagerActions.applyTermTranslationsChanges(data));
}

function* resetValidationState() {
  const rows: ITermTranslationRow[] = yield call(getNotValidRows);

  if (rows.length) {
    const updatedRecords = rows.map(r => {
      return getTranslationModifyData(r, [], r.data.translation, r.data.translationDefinition);
    });

    yield put(translationsManagerActions.applyTermTranslationsChanges(updatedRecords));
  }
}

function* getErrorRecords(error: HttpError) {
  const errorModels: ITermTranslationErrorModel[] = error.data;
  const rows: ITermTranslationRow[] = yield select(selectors.getGridRows);

  const updatedRecords: ITermTranslationModifyData[] = [];

  for (const row of rows) {
    const issues: IValidationIssue[] = [];

    for (const model of errorModels) {
      if (row.data.translationId === model.translationId) {
        issues.push({
          message: extractApiErrorMessageContent(model.error),
          severity: ValidationIssueSeverity.Error,
          tag: getValidationTag(model)
        });
      }
    }

    const data = getTranslationModifyData(row, issues, row.data.translation, row.data.translationDefinition);
    updatedRecords.push(data);
  }

  return updatedRecords;
}

function* initializeExecute() {
  try {
    const [statuses, sources]: [ILocalizable[], ILocalizable[]] = yield all([call(api.getTranslationStatuses), call(api.getTranslationSources)]);

    const filter: ITranslationsManagerFilter = {
      locales: [],
      industries: [],
      statuses: statuses,
      sources: sources
    };

    yield put(translationsManagerActions.initializeCompleted(filter));
  } catch (error) {
    yield put(translationsManagerActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* searchExecute() {
  try {
    const filter: ITranslationsManagerFilter = yield select(selectors.getFilter);
    const searchData = filter.lastData;

    if (searchData) {
      if (!searchData.selectedLocale) {
        yield put(translationsManagerActions.loadingFailed(notification.warning().useMessageId(glossaryMessages.error("localeRequired"))));
      } else if (isDefaultLocale(searchData.selectedLocale.localeIdentifier) && !searchData.selectedIndustry) {
        yield put(translationsManagerActions.loadingFailed(notification.warning().useMessageId(glossaryMessages.error("industryRequired"))));
      } else {
        const request: ITermTranslationsGetRequest = {
          locale: searchData.selectedLocale.localeIdentifier,
          industry: searchData.selectedIndustry && searchData.selectedIndustry.isiCv4Code,
          translationSource: searchData.selectedSource && searchData.selectedSource.messageId.value,
          translationStatus: searchData.selectedStatus && searchData.selectedStatus.messageId.value,
          textFilter: searchData.filterText
        };

        const termTranslations: ITermTranslationRelationship[] = yield call(api.getTermTranslations, request);

        const translationRows: ITermTranslationRow[] = termTranslations.map((item, index) => {
          return {
            data: item,
            index: index,
            isTranslationChanged: false,
            isTranslationDefinitionChanged: false,
            validationIssues: []
          };
        });

        yield put(translationsManagerActions.searchCompleted(translationRows));
      }
    }
  } catch (error) {
    yield put(translationsManagerActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* saveTermTranslationsExecute() {
  try {
    const filterData: ITranslationsManagerFilterData | undefined = yield select(selectors.getFilterLastData);

    if (filterData) {
      const rows: ITermTranslationRow[] = yield call(getChangedRows);

      const changedRows = rows.map(row => {
        const data = row.data;

        const rowData: ITermTranslationModel = {
          definition: data.translationDefinition,
          translation: data.translation,
          industry: filterData.selectedIndustry && filterData.selectedIndustry.isiCv4Code,
          locale: filterData.selectedLocale && filterData.selectedLocale.localeIdentifier,
          termId: data.termId,
          translationId: data.translationId || Guid.raw(),
          version: data.translationVersion
        };
        return rowData;
      });

      if (changedRows.length === 0) {
        yield call(resetValidationState);
        yield put(
          translationsManagerActions.saveTermTranslationsCompleted({ message: notification.warning().useMessageId(commonMessages.info("noRecordsModified")), isLoading: false })
        );
      } else {
        yield call(api.saveTermTranslations, changedRows);

        yield put(translationsManagerActions.search());
        yield put(
          translationsManagerActions.saveTermTranslationsCompleted({ message: notification.success().useMessageId(glossaryMessages.info("termTranslationsSaveCompleted")) })
        );
      }
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: ITermTranslationModifyData[] = yield call(getErrorRecords, error);

      yield put(translationsManagerActions.applyTermTranslationsChanges(updatedRecords));
    }

    yield put(translationsManagerActions.loadingFailed(notification.error(error).useMessageId(glossaryMessages.error("termTranslationsSaveFailed"))));
  }
}

function* approveTermTranslationsExecute() {
  try {
    const [rows, filterData]: [ITermTranslationRow[], ITranslationsManagerFilterData | undefined] = yield select((state: SelectorState) => [
      state.translationsManager.grid.selected,
      state.translationsManager.filter.lastData
    ]);

    if (filterData) {
      const selectedRows: ITermTranslationsApproveRequest[] = rows.map(row => {
        const data = row.data;

        return {
          industry: filterData.selectedIndustry && filterData.selectedIndustry.isiCv4Code,
          locale: (filterData.selectedLocale && filterData.selectedLocale.localeIdentifier) as string,
          termId: data.termId,
          translationId: data.translationId as string,
          version: data.translationVersion
        };
      });

      yield call(api.approveTermTranslations, selectedRows);

      yield put(translationsManagerActions.search());
      yield put(translationsManagerActions.approveTermTranslationsCompleted(notification.success().useMessageId(glossaryMessages.info("termTranslationsApproveCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: ITermTranslationModifyData[] = yield call(getErrorRecords, error);

      yield put(translationsManagerActions.applyTermTranslationsChanges(updatedRecords));
    }

    yield put(translationsManagerActions.loadingFailed(notification.error().useMessageId(glossaryMessages.error("termTranslationsApproveFailed"))));
  }
}

function* changeTranslationExecute({ payload }: PayloadAction<IChangeText>) {
  const rows: ITermTranslationRow[] = yield select(selectors.getGridRows);
  const row = rows[payload.index];

  const issues = row.validationIssues.filter(v => v.tag !== ValidationTags.Translation && v.tag !== ValidationTags.Both);
  const changedData = getTranslationModifyData(row, issues, payload.text, row.data.translationDefinition);

  yield put(translationsManagerActions.applyTermTranslationChanges(changedData));
}

function* changeTranslationDefinitionExecute({ payload }: PayloadAction<IChangeText>) {
  const rows: ITermTranslationRow[] = yield select(selectors.getGridRows);
  const row = rows[payload.index];

  const issues = row.validationIssues.filter(v => v.tag !== ValidationTags.Description && v.tag !== ValidationTags.Both);
  const changedData = getTranslationModifyData(row, issues, row.data.translation, payload.text);

  yield put(translationsManagerActions.applyTermTranslationChanges(changedData));
}

function* applyTermTranslationChangesExecute() {
  const rows: ITermTranslationRow[] = yield call(getChangedRows);
  const notifications: INotification[] = yield select(selectors.getNotifications);

  const warningMessageId = glossaryMessages.info("updatedItemsNotSaved");
  const warningShown = notifications.some(n => n.content.some(c => isLocalizable(c.message) && c.message.messageId.value === warningMessageId.value));

  if (rows.length > 0) {
    if (!warningShown) {
      yield put(translationsManagerActions.applyTermTranslationChangesWarning(notification.warning().useMessageId(warningMessageId)));
    }
  } else {
    if (warningShown) {
      yield put(translationsManagerActions.applyTermTranslationChangesWarning());
    }
  }
}

function* obsoleteTranslationsExecute() {
  const selectedRows: ITermTranslationRow[] = yield select(selectors.getSelectedGridRows);

  const changedData: ITermTranslationModifyData[] = selectedRows.map(row => getTranslationModifyData(row, [], "", ""));

  yield call(applyTermTranslationsChanges, changedData);
  yield call(applyTermTranslationChangesExecute);

  yield put(translationsManagerActions.obsoleteTermTranslationsCompleted());
}

function* loadIndustriesExecute({ payload }: PayloadAction<IPickerSearchCriterion<IIndustry>>) {
  try {
    let industries: IIndustry[] = yield call(api.getIndustries, payload.query.query);
    industries = applyPickerFilter(industries, payload, row => row.description);

    yield put(translationsManagerActions.loadIndustriesCompleted(industries));
  } catch (error) {
    yield put(translationsManagerActions.loadingFailed(notification.error(error).useMessageId(glossaryMessages.error("loadIndustriesFailed"))));
  }
}

function* loadSupportedLocalesExecute() {
  try {
    const locales: ILocale[] = yield call(api.getLocales);

    yield put(translationsManagerActions.loadSupportedLocalesCompleted(locales));
  } catch (error) {
    yield put(translationsManagerActions.loadSupportedLocalesFailed(notification.error(error).useMessageId(commonMessages.error("loadLocalesFailed"))));
  }
}

function* loadLocalesExecute({ payload }: PayloadAction<IPickerSearchCriterion<ILocale>>) {
  try {
    let locales: ILocale[] = yield call(api.getLocales, payload.query.query);
    locales = applyPickerFilter(locales, payload, row => row.name);
    applyPickerFilter(locales, payload, row => row.localeIdentifier);

    yield put(translationsManagerActions.loadLocalesCompleted(locales));
  } catch (error) {
    yield put(translationsManagerActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("loadLocalesFailed"))));
  }
}

function* exportTranslationsToXmlExecute() {
  try {
    const searchData: ITranslationsManagerFilterData | undefined = yield select(selectors.getFilterLastData);

    if (searchData) {
      const request: ITermTranslationsGetRequest = {
        locale: searchData.selectedLocale && searchData.selectedLocale.localeIdentifier,
        industry: searchData.selectedIndustry && searchData.selectedIndustry.isiCv4Code,
        translationSource: searchData.selectedSource && searchData.selectedSource.messageId.value,
        translationStatus: searchData.selectedStatus && searchData.selectedStatus.messageId.value,
        textFilter: searchData.filterText
      };

      yield call(api.exportTermTranslationsToXml, request);
    }
  } catch (error) {
    yield put(translationsManagerActions.loadingFailed(notification.error(error).useMessageId(glossaryMessages.error("exportFailed"))));
  }
}

function* downloadTermTranslationsUploadTemplateExecute() {
  try {
    yield call(api.downloadTermTranslationsUploadTemplate);
    yield put(translationsManagerActions.downloadTermTranslationsUploadTemplateCompleted());
  } catch (error) {
    yield put(translationsManagerActions.downloadTermTranslationsUploadTemplateFailed(notification.error().useMessageId(glossaryMessages.error("uploadTermTranslationsError"))));
  }
}

function* actionWatcher() {
  yield takeLatest(translationsManagerActions.initialize.toString(), initializeExecute);
  yield takeLatest(translationsManagerActions.search.toString(), searchExecute);
  yield takeLatest(translationsManagerActions.saveTermTranslations.toString(), saveTermTranslationsExecute);
  yield takeLatest(translationsManagerActions.obsoleteTermTranslations.toString(), obsoleteTranslationsExecute);
  yield takeLatest(translationsManagerActions.changeTranslation.toString(), changeTranslationExecute);
  yield takeLatest(translationsManagerActions.changeTranslationDefinition.toString(), changeTranslationDefinitionExecute);
  yield takeLatest(translationsManagerActions.loadIndustries.toString(), loadIndustriesExecute);
  yield takeLatest(translationsManagerActions.loadSupportedLocales.toString(), loadSupportedLocalesExecute);
  yield takeLatest(translationsManagerActions.loadLocales.toString(), loadLocalesExecute);
  yield takeLatest(translationsManagerActions.exportTranslationsToXml.toString(), exportTranslationsToXmlExecute);
  yield takeLatest(translationsManagerActions.downloadTermTranslationsUploadTemplate.toString(), downloadTermTranslationsUploadTemplateExecute);
  yield takeLatest(translationsManagerActions.approveTermTranslations.toString(), approveTermTranslationsExecute);
  yield takeLatest(translationsManagerActions.applyTermTranslationChanges.toString(), applyTermTranslationChangesExecute);
}

export function* translationsManagerSaga() {
  yield all([actionWatcher()]);
}
